import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { Shield, FileText, Mail, ArrowLeft } from "lucide-react";
import { openCookieSettings } from "@/components/CookieConsent";

const LAST_UPDATED = "2026-10-04";

type Doc = "privacy" | "terms";
type Lang = "th" | "en";

interface Section {
  title: string;
  lines: string[];
  cookieSettings?: boolean;
}

const CONTENT: Record<Doc, Record<Lang, { subtitle: string; sections: Section[] }>> = {
  privacy: {
    th: {
      subtitle: "เราเคารพข้อมูลส่วนบุคคลของคุณ และดำเนินการตามพระราชบัญญัติคุ้มครองข้อมูลส่วนบุคคล (PDPA)",
      sections: [
        {
          title: "1. ข้อมูลที่เราเก็บ",
          lines: [
            "อีเมลและชื่อบัญชีจากการเข้าสู่ระบบด้วย Google",
            "เนื้อหาและ prompts ที่คุณสร้างผ่านระบบ AI",
            "ข้อมูลเครดิตคงเหลือและประวัติการสร้างเนื้อหา",
            "ข้อมูลการชำระเงินผ่านผู้ให้บริการมาตรฐาน โดยเราไม่จัดเก็บเลขบัตรเครดิตเต็มหมายเลข",
          ],
        },
        {
          title: "2. วัตถุประสงค์การใช้ข้อมูล",
          lines: [
            "ให้บริการระบบ AI สำหรับสร้างเนื้อหา",
            "ปรับปรุงคุณภาพและความเสถียรของระบบ",
            "แจ้งเตือนเกี่ยวกับบัญชีและบริการ",
            "ประมวลผลการชำระเงินและจัดการเครดิต",
          ],
        },
        {
          title: "3. การเปิดเผยต่อบุคคลที่สาม",
          lines: [
            "เราไม่ขายข้อมูลของคุณให้บุคคลที่สามใดๆ",
            "ใช้ผู้ให้บริการภายนอกเฉพาะที่จำเป็น เช่น ระบบ AI processing, authentication และ payment gateway",
            "ผู้ให้บริการเหล่านั้นอยู่ภายใต้ข้อตกลงการรักษาความลับและประมวลผลข้อมูลตามมาตรฐาน",
          ],
        },
        {
          title: "4. คุกกี้และเครื่องมือวัดผล",
          cookieSettings: true,
          lines: [
            "คุกกี้ที่จำเป็น: ใช้ให้เว็บทำงาน เช่น การเข้าสู่ระบบ ยอดเครดิต ภาษา และความปลอดภัย เพื่อให้บริการตามที่คุณขอ จึงไม่ต้องขอความยินยอม",
            "การวัดการใช้งานภายในของ AIM: เก็บในระบบของเราเองเพื่อปรับปรุงบริการ ไม่ส่งให้บุคคลภายนอก โดยอาศัยประโยชน์โดยชอบด้วยกฎหมาย และคุณคัดค้านได้ตามข้อ 5",
            "คุกกี้วิเคราะห์ (Google Analytics) และคุกกี้การตลาด (Meta Pixel, TikTok Pixel, LINE Tag และ OpenAI Ads) ใช้เมื่อคุณให้ความยินยอมเท่านั้น เราจะไม่โหลดจนกว่าคุณกดยอมรับ",
            "คุกกี้การตลาดอาจส่งอีเมลหรือรหัสผู้ใช้ที่ผ่านการเข้ารหัสแบบทางเดียว (hash) ให้ผู้ให้บริการโฆษณาเพื่อจับคู่ผลลัพธ์ ผู้ให้บริการบางรายอยู่ต่างประเทศ ข้อมูลจึงอาจถูกประมวลผลนอกประเทศไทย",
            "คุณเปลี่ยนหรือถอนความยินยอมได้ทุกเมื่อ ง่ายเท่ากับตอนให้ ผ่าน \"ตั้งค่าคุกกี้\" ท้ายหน้าเว็บหรือปุ่มด้านล่างนี้ การถอนไม่กระทบการใช้งานเว็บ และไม่กระทบข้อมูลที่ส่งไปก่อนถอน",
            "ผู้ให้บริการแต่ละรายเก็บข้อมูลตามระยะเวลาและนโยบายของตนเอง และคุณลบคุกกี้ได้จากการตั้งค่าเบราว์เซอร์",
          ],
        },
        {
          title: "5. สิทธิเจ้าของข้อมูล (PDPA)",
          lines: [
            "ได้รับแจ้งว่าเราเก็บข้อมูลอะไรและเพื่ออะไร ตามนโยบายนี้",
            "ถอนความยินยอมได้ทุกเมื่อ",
            "ขอเข้าถึงและขอรับสำเนาข้อมูลของคุณ",
            "ขอให้โอนข้อมูลของคุณไปยังผู้ให้บริการรายอื่น",
            "คัดค้านการเก็บ ใช้ หรือเปิดเผยข้อมูล รวมถึงการตลาดแบบตรง",
            "ขอให้ลบหรือทำลายข้อมูล",
            "ขอให้ระงับการใช้ข้อมูลไว้ก่อน",
            "ขอแก้ไขข้อมูลให้ถูกต้องและเป็นปัจจุบัน",
            "ร้องเรียนต่อสำนักงานคณะกรรมการคุ้มครองข้อมูลส่วนบุคคล",
            "ใช้สิทธิได้ทางอีเมลในข้อ 7 · คำขอเข้าถึงหรือขอสำเนาข้อมูล เราจะดำเนินการภายใน 30 วันนับจากวันที่ได้รับคำขอ",
          ],
        },
        {
          title: "6. การเก็บรักษาและความปลอดภัย",
          lines: [
            "ข้อมูลถูกเข้ารหัสทั้งระหว่างการส่งและการจัดเก็บ",
            "เข้าถึงได้เฉพาะบุคลากรที่ได้รับอนุญาตเท่านั้น",
            "เก็บรักษาข้อมูลเท่าที่จำเป็นต่อการให้บริการและตามที่กฎหมายกำหนด",
          ],
        },
        {
          title: "7. การติดต่อ",
          lines: ["หากมีข้อสงสัยเกี่ยวกับนโยบายความเป็นส่วนตัว กรุณาติดต่อ aimacademy.co@gmail.com"],
        },
      ],
    },
    en: {
      subtitle: "We respect your personal data and comply with Thailand's Personal Data Protection Act (PDPA).",
      sections: [
        {
          title: "1. Information We Collect",
          lines: [
            "Email and account name from Google sign-in",
            "Content and prompts you create through our AI system",
            "Credit balance and content generation history",
            "Payment information processed through standard providers; we do not store full credit card numbers",
          ],
        },
        {
          title: "2. Purpose of Use",
          lines: [
            "To provide AI content generation services",
            "To improve system quality and stability",
            "To send account and service notifications",
            "To process payments and manage credits",
          ],
        },
        {
          title: "3. Disclosure to Third Parties",
          lines: [
            "We do not sell your data to any third party",
            "We use external providers only as necessary, such as AI processing, authentication, and payment gateways",
            "Such providers operate under confidentiality agreements and standard data processing terms",
          ],
        },
        {
          title: "4. Cookies and Measurement Tools",
          cookieSettings: true,
          lines: [
            "Essential cookies keep the site working, e.g. sign-in, credit balance, language and security. We need them to provide the service you ask for, so they don't require consent.",
            "AIM's own usage measurement stays in our systems to improve the service and is not shared with third parties. It relies on our legitimate interests, and you can object under section 5.",
            "Analytics cookies (Google Analytics) and marketing cookies (Meta Pixel, TikTok Pixel, LINE Tag and OpenAI Ads) are used only with your consent. We don't load them until you accept.",
            "Marketing cookies may send a one-way hashed email or user ID to ad providers to match results. Some providers are located abroad, so this data may be processed outside Thailand.",
            "You can change or withdraw consent anytime, as easily as you gave it, via \"Cookie settings\" at the bottom of the page or the button below. Withdrawing doesn't affect your use of the site or data sent before you withdrew.",
            "Each provider keeps data for its own retention period under its own policy, and you can delete cookies in your browser settings.",
          ],
        },
        {
          title: "5. Data Subject Rights (PDPA)",
          lines: [
            "Be informed of what we collect and why, as set out in this policy",
            "Withdraw consent at any time",
            "Access and obtain a copy of your data",
            "Have your data transferred to another provider",
            "Object to collection, use or disclosure, including direct marketing",
            "Have your data deleted or destroyed",
            "Have the use of your data restricted",
            "Have your data corrected and kept up to date",
            "Lodge a complaint with the Personal Data Protection Committee office",
            "To use these rights, email us at the address in section 7. We respond to access and copy requests within 30 days of receiving them.",
          ],
        },
        {
          title: "6. Retention and Security",
          lines: [
            "Data is encrypted both in transit and at rest",
            "Access is restricted to authorized personnel only",
            "Data is retained only as long as necessary for service delivery and as required by law",
          ],
        },
        {
          title: "7. Contact",
          lines: ["For questions about this privacy policy, please contact aimacademy.co@gmail.com"],
        },
      ],
    },
  },
  terms: {
    th: {
      subtitle: "ข้อกำหนดและเงื่อนไขการใช้บริการแพลตฟอร์ม AIM Academy",
      sections: [
        {
          title: "1. การยอมรับข้อกำหนด",
          lines: ["การเข้าใช้งานแพลตฟอร์มถือว่าคุณได้อ่าน ทำความเข้าใจ และยอมรับข้อกำหนดเหล่านี้ทั้งหมด"],
        },
        {
          title: "2. บัญชีผู้ใช้",
          lines: [
            "คุณเป็นผู้รับผิดชอบในการรักษาความปลอดภัยของบัญชีและรหัสผ่าน",
            "การกระทำใดๆ ภายใต้บัญชีของคุณถือเป็นความรับผิดชอบของคุณ",
          ],
        },
        {
          title: "3. การใช้งานที่อนุญาต",
          lines: [
            "ห้ามสร้างเนื้อหาที่ผิดกฎหมายหรือละเมิดสิทธิของผู้อื่น",
            "ห้ามพยายามเข้าถึงระบบโดยไม่ได้รับอนุญาต",
            "ห้ามขายต่อบัญชีหรือเครดิตให้บุคคลอื่น",
          ],
        },
        {
          title: "4. เครดิตและการชำระเงิน",
          lines: [
            "เครดิตแบบ top-up ไม่มีวันหมดอายุ",
            "เครดิตรายเดือนเป็นไปตามเงื่อนไขของแผนและการต่ออายุ",
            "การชำระเงินถือเป็นที่สิ้นสุด ไม่สามารถขอคืนเงินได้ยกเว้นกรณีที่กฎหมายกำหนด",
          ],
        },
        {
          title: "5. ทรัพย์สินทางปัญญา",
          lines: [
            "เนื้อหาที่ผู้ใช้สร้างขึ้นเป็นกรรมสิทธิ์ของผู้ใช้",
            "ระบบ เครื่องมือ ดีไซน์ และเทคโนโลยีของแพลตฟอร์มเป็นกรรมสิทธิ์ของ AIM Academy",
          ],
        },
        {
          title: "6. ข้อจำกัดความรับผิด",
          lines: [
            "บริการให้ในรูปแบบ as-is ตามสภาพการใช้งานจริง",
            "ผลลัพธ์จาก AI อาจไม่ถูกต้องหรือสมบูรณ์เสมอไป ผู้ใช้ควรตรวจสอบก่อนนำไปใช้",
            "เราไม่รับผิดชอบความเสียหายที่เกิดจากการใช้งานเนื้อหาที่ AI สร้างขึ้น",
          ],
        },
        {
          title: "7. การเปลี่ยนแปลงข้อกำหนด",
          lines: [
            "เราอาจปรับปรุงข้อกำหนดเป็นครั้งคราว",
            "การใช้งานต่อหลังการปรับปรุงถือเป็นการยอมรับข้อกำหนดฉบับใหม่",
          ],
        },
        {
          title: "8. การติดต่อ",
          lines: ["หากมีข้อสงสัยเกี่ยวกับข้อกำหนดการใช้งาน กรุณาติดต่อ salesarmbiz@gmail.com"],
        },
      ],
    },
    en: {
      subtitle: "Terms and conditions for using the AIM Academy platform.",
      sections: [
        {
          title: "1. Acceptance of Terms",
          lines: ["By accessing the platform, you confirm that you have read, understood, and agree to these terms in full."],
        },
        {
          title: "2. User Accounts",
          lines: [
            "You are responsible for maintaining the security of your account and password",
            "Any activity under your account is your responsibility",
          ],
        },
        {
          title: "3. Permitted Use",
          lines: [
            "Do not generate unlawful content or content that infringes the rights of others",
            "Do not attempt unauthorized access to the system",
            "Do not resell accounts or credits to other parties",
          ],
        },
        {
          title: "4. Credits and Payment",
          lines: [
            "Top-up credits do not expire",
            "Monthly credits follow the terms of your plan and renewal cycle",
            "Payments are final and non-refundable except where required by law",
          ],
        },
        {
          title: "5. Intellectual Property",
          lines: [
            "Content generated by users belongs to the respective users",
            "The platform's system, tools, design, and technology are the property of AIM Academy",
          ],
        },
        {
          title: "6. Limitation of Liability",
          lines: [
            "The service is provided on an as-is basis",
            "AI-generated outputs may not always be accurate or complete; users should verify before use",
            "We are not liable for damages arising from use of AI-generated content",
          ],
        },
        {
          title: "7. Changes to Terms",
          lines: [
            "We may update these terms from time to time",
            "Continued use after updates constitutes acceptance of the new terms",
          ],
        },
        {
          title: "8. Contact",
          lines: ["For questions about these terms of use, please contact salesarmbiz@gmail.com"],
        },
      ],
    },
  },
};

const TITLES: Record<Doc, Record<Lang, string>> = {
  privacy: { th: "นโยบายความเป็นส่วนตัว", en: "Privacy Policy" },
  terms: { th: "ข้อกำหนดการใช้งาน", en: "Terms of Use" },
};

const PAGE_TITLES: Record<Doc, string> = {
  privacy: "นโยบายความเป็นส่วนตัว | AIM Academy",
  terms: "ข้อกำหนดการใช้งาน | AIM Academy",
};

function formatDate(lang: Lang): string {
  if (lang === "th") return "4 ตุลาคม 2569";
  return "October 4, 2026";
}

interface LegalPageProps {
  doc: Doc;
}

export default function LegalPage({ doc }: LegalPageProps) {
  const [lang, setLang] = useState<Lang>("th");
  const isPrivacy = doc === "privacy";
  const accent = isPrivacy ? "#05F2F2" : "#F27405";
  const Icon = isPrivacy ? Shield : FileText;
  const data = CONTENT[doc][lang];
  const title = TITLES[doc][lang];

  const labelBack = lang === "th" ? "← กลับหน้าหลัก" : "← Back to home";
  const labelUpdated = lang === "th" ? "อัปเดตล่าสุด" : "Last updated";
  const labelContactCard = lang === "th" ? "ติดต่อเรา" : "Contact us";
  const crossHref = isPrivacy ? "/terms" : "/privacy";
  const crossLabel = isPrivacy
    ? lang === "th"
      ? "อ่านข้อกำหนดการใช้งาน →"
      : "Read Terms of Use →"
    : lang === "th"
      ? "อ่านนโยบายความเป็นส่วนตัว →"
      : "Read Privacy Policy →";

  void LAST_UPDATED;

  return (
    <>
      <Helmet>
        <title>{PAGE_TITLES[doc]}</title>
      </Helmet>
      <div style={{ minHeight: "100vh", background: "#0A1A26", color: "#C9D6DE" }}>
        <div
          style={{
            maxWidth: 760,
            margin: "0 auto",
            padding: "48px 24px 96px",
            position: "relative",
          }}
        >
          {/* Top bar */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: 32,
              gap: 16,
              flexWrap: "wrap",
            }}
          >
            <a
              href="/"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                color: "#6593A6",
                fontSize: 14,
                textDecoration: "none",
              }}
            >
              <ArrowLeft size={14} />
              {lang === "th" ? "กลับหน้าหลัก" : "Back to home"}
            </a>

            {/* Language toggle */}
            <div
              style={{
                display: "inline-flex",
                background: "#012840",
                borderRadius: 999,
                padding: 4,
                gap: 2,
              }}
              role="group"
              aria-label="Language toggle"
            >
              {(["th", "en"] as Lang[]).map((l) => {
                const active = lang === l;
                return (
                  <button
                    key={l}
                    type="button"
                    onClick={() => setLang(l)}
                    style={{
                      padding: "6px 14px",
                      borderRadius: 999,
                      fontSize: 12,
                      fontWeight: 600,
                      letterSpacing: "0.05em",
                      background: active ? "#05F2F2" : "transparent",
                      color: active ? "#0A1A26" : "#6593A6",
                      border: "none",
                      cursor: "pointer",
                      transition: "all 150ms ease",
                    }}
                    aria-pressed={active}
                  >
                    {l.toUpperCase()}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Header */}
          <div style={{ marginBottom: 24 }}>
            <div
              style={{
                width: 56,
                height: 56,
                borderRadius: 14,
                background: "#012840",
                border: "1px solid #05F2F233",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                marginBottom: 20,
              }}
            >
              <Icon size={26} color={accent} />
            </div>
            <h1
              style={{
                color: "#FFFFFF",
                fontSize: 30,
                fontWeight: 700,
                margin: 0,
                lineHeight: 1.3,
              }}
            >
              {title}
            </h1>
            <p style={{ color: "#6593A6", fontSize: 15, marginTop: 10, lineHeight: 1.6 }}>
              {data.subtitle}
            </p>
            <div
              style={{
                marginTop: 18,
                height: 3,
                width: 64,
                background: accent,
                borderRadius: 2,
              }}
            />
            <p style={{ color: "#6593A6", fontSize: 13, marginTop: 16 }}>
              {labelUpdated}: {formatDate(lang)}
            </p>
          </div>

          {/* Sections */}
          <div style={{ marginTop: 40 }}>
            {data.sections.map((section) => (
              <section key={section.title} style={{ marginBottom: 36 }}>
                <h2
                  style={{
                    color: "#FFFFFF",
                    fontSize: 19,
                    fontWeight: 600,
                    margin: "0 0 14px 0",
                    lineHeight: 1.5,
                  }}
                >
                  {section.title}
                </h2>
                {section.lines.length === 1 ? (
                  <p
                    style={{
                      color: "#C9D6DE",
                      fontSize: 15.5,
                      lineHeight: 1.75,
                      margin: 0,
                    }}
                  >
                    {section.lines[0]}
                  </p>
                ) : (
                  <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                    {section.lines.map((line, idx) => (
                      <li
                        key={idx}
                        style={{
                          color: "#C9D6DE",
                          fontSize: 15.5,
                          lineHeight: 1.75,
                          paddingLeft: 22,
                          position: "relative",
                          marginBottom: 6,
                        }}
                      >
                        <span
                          style={{
                            position: "absolute",
                            left: 0,
                            top: 0,
                            color: accent,
                            fontWeight: 600,
                          }}
                          aria-hidden
                        >
                          —
                        </span>
                        {line}
                      </li>
                    ))}
                  </ul>
                )}
                {section.cookieSettings && (
                  <button
                    type="button"
                    onClick={openCookieSettings}
                    style={{
                      marginTop: 14,
                      minHeight: 44,
                      padding: "0 20px",
                      borderRadius: 12,
                      border: `1px solid ${accent}`,
                      background: "transparent",
                      color: accent,
                      fontSize: 15,
                      fontWeight: 500,
                      cursor: "pointer",
                    }}
                  >
                    {lang === "th" ? "ตั้งค่าคุกกี้" : "Cookie settings"}
                  </button>
                )}
              </section>
            ))}
          </div>

          {/* Contact card */}
          <div
            style={{
              background: "#012840",
              border: "1px solid #05F2F233",
              borderRadius: 14,
              padding: "20px 22px",
              display: "flex",
              alignItems: "center",
              gap: 14,
              marginTop: 16,
            }}
          >
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: 10,
                background: "#0A1A26",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <Mail size={18} color="#05F2F2" />
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ color: "#6593A6", fontSize: 12, marginBottom: 2 }}>
                {labelContactCard}
              </div>
              <a
                href="mailto:salesarmbiz@gmail.com"
                style={{
                  color: "#FFFFFF",
                  fontSize: 15,
                  fontWeight: 500,
                  textDecoration: "none",
                  wordBreak: "break-all",
                }}
              >
                salesarmbiz@gmail.com
              </a>
            </div>
          </div>

          {/* Cross link */}
          <div style={{ marginTop: 32, textAlign: "center" }}>
            <a
              href={crossHref}
              style={{
                color: accent,
                fontSize: 14,
                fontWeight: 500,
                textDecoration: "none",
              }}
            >
              {crossLabel}
            </a>
          </div>

          {/* Footer */}
          <footer
            style={{
              marginTop: 40,
              paddingTop: 24,
              borderTop: "1px solid #05F2F21A",
              textAlign: "center",
              color: "#6593A699",
              fontSize: 12.5,
            }}
          >
            © 2025 AIM Academy · aimacademy.co
          </footer>
        </div>
      </div>
    </>
  );
}
